/** Pure mixing policy — no Web Audio imports, so it runs under vitest.
 * The AudioLayer consults these before actually making noise. */

export type Priority = 'low' | 'normal' | 'high';

/** Global simultaneous-voice budget. Low-priority sounds yield headroom to
 * important ones; high-priority sounds always play (the master compressor
 * guards the sum). */
export const VOICE_CAP = 14;
const LOW_PRIORITY_HEADROOM = 4;

export function admitVoice(activeVoices: number, priority: Priority): boolean {
  if (priority === 'high') return true;
  if (priority === 'low') return activeVoices < VOICE_CAP - LOW_PRIORITY_HEADROOM;
  return activeVoices < VOICE_CAP;
}

/** Minimum ms between plays, per sound. Bursty events collapse into one. */
export const COOLDOWN_MS: Record<string, number> = {
  uiTick: 60,
  clink: 90,
  oreDrop: 220,
  placeThunk: 120,
  demolishCrumble: 250,
  paperSlide: 400,
  saleChaChing: 1000,
  coinSpill: 1500,
  truckOut: 2000,
  busArrival: 3000,
  trainWhistle: 5000,
  schoolBell: 2500,
  chapelBell: 2500,
  stamp: 1000,
  pressSting: 10000,
  deny: 300,
};

export class CooldownLimiter {
  private readonly lastPlayed = new Map<string, number>();

  /** True if `name` may play at `nowMs`; records the play when admitted. */
  admit(name: string, nowMs: number): boolean {
    const wait = COOLDOWN_MS[name] ?? 0;
    const last = this.lastPlayed.get(name);
    if (last !== undefined && nowMs - last < wait) return false;
    this.lastPlayed.set(name, nowMs);
    return true;
  }
}

/** Expected pickaxe clinks per second for a mining crew of `count`.
 * Saturates: one digger is an occasional clink, two hundred is a steady
 * worksite rhythm, never a hailstorm. */
export function clinkRate(count: number): number {
  if (count <= 0) return 0;
  return 3.5 * (1 - Math.exp(-count / 6));
}
